/**
 * 한국 행정구역 매핑 유틸리티
 * 대회 데이터의 city 값을 GeoJSON 표준 행정구역명으로 매핑
 */

/** GeoJSON 행정구역 코드 → 이름 */
export const REGION_CODES: Record<string, string> = {
  '11': '서울특별시',
  '21': '부산광역시',
  '22': '대구광역시',
  '23': '인천광역시',
  '24': '광주광역시',
  '25': '대전광역시',
  '26': '울산광역시',
  '29': '세종특별자치시',
  '31': '경기도',
  '32': '강원도',
  '33': '충청북도',
  '34': '충청남도',
  '35': '전라북도',
  '36': '전라남도',
  '37': '경상북도',
  '38': '경상남도',
  '39': '제주특별자치도',
};

/** 행정구역 → 짧은 라벨 */
const REGION_LABELS: Record<string, string> = {
  '서울특별시': '서울',
  '부산광역시': '부산',
  '대구광역시': '대구',
  '인천광역시': '인천',
  '광주광역시': '광주',
  '대전광역시': '대전',
  '울산광역시': '울산',
  '세종특별자치시': '세종',
  '경기도': '경기',
  '강원도': '강원',
  '충청북도': '충북',
  '충청남도': '충남',
  '전라북도': '전북',
  '전라남도': '전남',
  '경상북도': '경북',
  '경상남도': '경남',
  '제주특별자치도': '제주',
};

/** 행정구역 → 로마자 표기 (보조 eyebrow 표시용) */
const REGION_ROMAN: Record<string, string> = {
  '서울특별시': 'SEOUL',
  '부산광역시': 'BUSAN',
  '대구광역시': 'DAEGU',
  '인천광역시': 'INCHEON',
  '광주광역시': 'GWANGJU',
  '대전광역시': 'DAEJEON',
  '울산광역시': 'ULSAN',
  '세종특별자치시': 'SEJONG',
  '경기도': 'GYEONGGI',
  '강원도': 'GANGWON',
  '충청북도': 'CHUNGBUK',
  '충청남도': 'CHUNGNAM',
  '전라북도': 'JEONBUK',
  '전라남도': 'JEONNAM',
  '경상북도': 'GYEONGBUK',
  '경상남도': 'GYEONGNAM',
  '제주특별자치도': 'JEJU',
};

/** 접두어 → 표준 행정구역명 매핑 */
const PREFIX_MAP: [string, string][] = [
  ['서울특별시', '서울특별시'],
  ['서울시', '서울특별시'],
  ['부산광역시', '부산광역시'],
  ['대구광역시', '대구광역시'],
  ['인천광역시', '인천광역시'],
  ['광주광역시', '광주광역시'],
  ['대전광역시', '대전광역시'],
  ['울산광역시', '울산광역시'],
  ['세종특별자치시', '세종특별자치시'],
  ['경기도', '경기도'],
  ['강원특별자치도', '강원도'],
  ['강원도', '강원도'],
  ['충청북도', '충청북도'],
  ['충청남도', '충청남도'],
  ['전북특별자치도', '전라북도'],
  ['전라북도', '전라북도'],
  ['전라남도', '전라남도'],
  ['전남', '전라남도'],
  ['경상북도', '경상북도'],
  ['경상남도', '경상남도'],
  ['경남', '경상남도'],
  ['제주특별자치도', '제주특별자치도'],
];

/** 단독 도시명 → 표준 행정구역명 매핑 */
const CITY_DIRECT_MAP: Record<string, string> = {
  '서울': '서울특별시',
  '부산': '부산광역시',
  '대구': '대구광역시',
  '인천': '인천광역시',
  '광주광역시': '광주광역시',
  '대전광역시': '대전광역시',
  '울산광역시': '울산광역시',
  '부산광역시': '부산광역시',
  '대구광역시': '대구광역시',
  '인천광역시': '인천광역시',
  '세종특별자치시': '세종특별자치시',
  '제주특별자치도': '제주특별자치도',
  '경주': '경상북도',
  '경주시': '경상북도',
  '고양': '경기도',
  '구례': '전라남도',
  '구미': '경상북도',
  '군산': '전라북도',
  '군산시': '전라북도',
  '김포시': '경기도',
  '나주시': '전라남도',
  '남양주시': '경기도',
  '남해군': '경상남도',
  '단양': '충청북도',
  '부천': '경기도',
  '성남시': '경기도',
  '수원': '경기도',
  '아산시': '충청남도',
  '여수시': '전라남도',
  '영광군': '전라남도',
  '의성군': '경상북도',
  '이천시': '경기도',
  '장수': '전라북도',
  '장흥군': '전라남도',
  '정읍시': '전라북도',
  '제주시': '제주특별자치도',
  '창원시': '경상남도',
  '청주': '충청북도',
  '춘천': '강원도',
};

/** 여러 지역에 걸친 값 → 대표 행정구역 매핑 */
const MULTI_REGION_MAP: Record<string, string> = {
  '서울-파주': '서울특별시',
  '광양시/하동군': '전라남도',
};

/**
 * city 값을 GeoJSON 표준 행정구역명으로 매핑
 * @returns 표준 행정구역명 또는 매핑 불가 시 null
 */
export function getRegion(city: string): string | null {
  if (!city || city.trim() === '') return null;

  const trimmed = city.trim();

  // 1단계: 여러 지역 분할 케이스
  if (MULTI_REGION_MAP[trimmed]) {
    return MULTI_REGION_MAP[trimmed];
  }

  // 2단계: 접두어 매핑 (공백 포함된 "경기도 고양시" 등)
  for (const [prefix, region] of PREFIX_MAP) {
    if (trimmed.startsWith(prefix)) {
      return region;
    }
  }

  // 3단계: 단독 도시명 직접 매핑
  if (CITY_DIRECT_MAP[trimmed]) {
    return CITY_DIRECT_MAP[trimmed];
  }

  return null;
}

/**
 * 행정구역명의 짧은 라벨 반환
 */
export function getRegionLabel(region: string): string {
  return REGION_LABELS[region] ?? region;
}

/**
 * 행정구역명의 로마자 표기 반환 (매핑 없으면 빈 문자열)
 */
export function getRegionRoman(region: string): string {
  return REGION_ROMAN[region] ?? '';
}

/**
 * 전체 행정구역 목록 반환
 */
export function getAllRegions(): { code: string; name: string; label: string }[] {
  return Object.entries(REGION_CODES).map(([code, name]) => ({
    code,
    name,
    label: REGION_LABELS[name] ?? name,
  }));
}
